/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import type * as React from "react";
import type { Appearance } from "@stripe/stripe-js";

/**
 * S-4 (S697): the form field's concrete values, in ONE place.
 *
 * Account Settings draws two cards side by side at the wide breakpoint:
 * Personal Information on shadcn inputs, Billing Address on Stripe's Address
 * Element. They did not match -- different borders, different padding, and,
 * most visibly, different focus treatments (a blue border against a two-pixel
 * indigo ring). Two focus styles on one page is the difference a customer
 * notices first even if they could not name it.
 *
 * The values below were already in this file; what is new is that they are
 * named once and read twice. `stripeAppearance` is built from them, and
 * `formFieldCssVariables` hands the same numbers to ordinary CSS as custom
 * properties, which the shadcn inputs read (see `.settings-input` in
 * index.css). One place to change, and the two cards cannot drift again.
 *
 * The values are duplicated INTO the appearance object rather than referenced
 * from it because Stripe Elements renders inside a cross-origin iframe: it
 * cannot read a CSS custom property defined on this page, and it only accepts
 * literal strings. So the direction of travel is TypeScript to both consumers,
 * never CSS to Stripe.
 */
export const FORM_FIELD_TOKENS = {
  borderColor: "#939393",
  borderRadius: "10px",
  fontSize: "16px",
  fontWeight: "400",
  paddingY: "14px",
  paddingX: "16px",
  focusColor: "#6366f1",
  focusRingWidth: "2px",
  placeholderColor: "#7C7C7C",
  labelFontSize: "14px",
  labelFontWeight: "500",
  labelMarginBottom: "4px",
  errorColor: "#ef4444",
  errorFontSize: "14px",
  errorMarginTop: "4px",
  disabledOpacity: "0.5",
} as const;

/**
 * The same values as CSS custom properties, applied to the Account Settings
 * container so every shadcn control inside it resolves them.
 */
export const formFieldCssVariables: React.CSSProperties = {
  "--ds-field-border-color": FORM_FIELD_TOKENS.borderColor,
  "--ds-field-radius": FORM_FIELD_TOKENS.borderRadius,
  "--ds-field-font-size": FORM_FIELD_TOKENS.fontSize,
  "--ds-field-font-weight": FORM_FIELD_TOKENS.fontWeight,
  "--ds-field-padding-y": FORM_FIELD_TOKENS.paddingY,
  "--ds-field-padding-x": FORM_FIELD_TOKENS.paddingX,
  "--ds-field-focus-color": FORM_FIELD_TOKENS.focusColor,
  "--ds-field-focus-ring-width": FORM_FIELD_TOKENS.focusRingWidth,
  "--ds-field-placeholder-color": FORM_FIELD_TOKENS.placeholderColor,
  "--ds-label-font-size": FORM_FIELD_TOKENS.labelFontSize,
  "--ds-label-font-weight": FORM_FIELD_TOKENS.labelFontWeight,
  "--ds-label-margin-bottom": FORM_FIELD_TOKENS.labelMarginBottom,
  "--ds-field-error-color": FORM_FIELD_TOKENS.errorColor,
  "--ds-field-error-font-size": FORM_FIELD_TOKENS.errorFontSize,
  "--ds-field-error-margin-top": FORM_FIELD_TOKENS.errorMarginTop,
  "--ds-field-disabled-opacity": FORM_FIELD_TOKENS.disabledOpacity,
} as React.CSSProperties;

/**
 * Stripe Elements appearance configuration.
 * Provides a consistent visual style across all Stripe-powered form elements,
 * matching the Deskshare design system (rounded inputs, indigo focus ring,
 * medium-weight labels).
 *
 * S-4 (S697): every value is read from FORM_FIELD_TOKENS above. The object's
 * SHAPE and the strings it resolves to are unchanged -- this is the same
 * appearance the cart has always sent Stripe.
 */
export const stripeAppearance: Appearance = {
  theme: "flat",
  variables: {
    fontSizeBase: FORM_FIELD_TOKENS.fontSize,
    borderRadius: FORM_FIELD_TOKENS.borderRadius,
    colorText: "#000",
    colorTextPlaceholder: FORM_FIELD_TOKENS.placeholderColor,
    focusBoxShadow: `0 0 0 ${FORM_FIELD_TOKENS.focusRingWidth} ${FORM_FIELD_TOKENS.focusColor}`,
    focusOutline: "none",
    colorPrimary: FORM_FIELD_TOKENS.focusColor,
    colorIconCardCvc: FORM_FIELD_TOKENS.focusColor,
  },
  rules: {
    ".Input": {
      border: `1px solid ${FORM_FIELD_TOKENS.borderColor}`,
      borderRadius: FORM_FIELD_TOKENS.borderRadius,
      fontWeight: FORM_FIELD_TOKENS.fontWeight,
      padding: `${FORM_FIELD_TOKENS.paddingY} ${FORM_FIELD_TOKENS.paddingX}`,
      fontSize: FORM_FIELD_TOKENS.fontSize,
    },
    ".Input:disabled": {
      opacity: FORM_FIELD_TOKENS.disabledOpacity,
      cursor: "not-allowed",
    },
    ".Input:focus": {
      outline: "none",
      boxShadow: `0 0 0 ${FORM_FIELD_TOKENS.focusRingWidth} ${FORM_FIELD_TOKENS.focusColor}`,
      borderColor: FORM_FIELD_TOKENS.focusColor,
    },
    ".Input::placeholder": {
      color: FORM_FIELD_TOKENS.placeholderColor,
      fontWeight: "300",
    },
    ".Label": {
      fontSize: FORM_FIELD_TOKENS.labelFontSize,
      fontWeight: FORM_FIELD_TOKENS.labelFontWeight,
      marginBottom: FORM_FIELD_TOKENS.labelMarginBottom,
    },
    ".Error": {
      color: FORM_FIELD_TOKENS.errorColor,
      fontSize: FORM_FIELD_TOKENS.errorFontSize,
      marginTop: FORM_FIELD_TOKENS.errorMarginTop,
    },
  },
};
